import * as React from 'react'
import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react'
import { DataPromise, Destination, getDataEmptyState, IGraphPayload } from './AxiosContext'
import FileContext from '../context/FileContext'
import useEntity from '../hooks/useEntity'
import { Message } from '@microsoft/microsoft-graph-types'
import { extractReferenceNumbersFromMessage, FILE_REFERENCE_NUMBER_PROP } from '../helpers/referenceNumberHelper'
import { isOfficeInitialized } from '../helpers/outlookHelper'
import { useTranslation } from 'react-i18next'
import AuthorizationContext from './AuthorizationContext'

export const MAX_MESSAGE_SIZE = 36_700_160 //max size or fallback to 35MB.

export type EmailAddressDto = {
    name: string
    address: string
}

export type MessageDto = {
    internetMessageId: string
    conversationId: string
    subject: string
    sender: EmailAddressDto
    recipients: EmailAddressDto[]
}

export const mapEmailUser = (details: Office.EmailAddressDetails | Office.EmailUser): EmailAddressDto => ({
    name: details.displayName,
    address: details.emailAddress?.trim(),
})

export const mapOutlookItem = (item: Office.MessageRead): MessageDto => ({
    internetMessageId: item.internetMessageId,
    conversationId: item.conversationId,
    subject: item.subject,
    sender: mapEmailUser(item.sender),
    recipients: item.to.map(recipient => mapEmailUser(recipient)),
})

export type MessageContextType = {
    outlookMessage: Office.MessageRead | Office.MessageCompose // User selects an e-mail in Outlook
    message: DataPromise<Message> // HAMMOCK sets the Graph API representation
    resetMessage: () => void
    fileIdMap?: Map<string, string> // If and only if fileIdMap is undefined, the mail has not been processed yet.
    messageRestImmutableId: string //The immutable ID is set. Use this as change trigger.
    maxMessageSize: DataPromise<number> // Mailbox message-size limit in bytes.
}

const MessageContext = createContext<MessageContextType>({
    outlookMessage: undefined,
    message: getDataEmptyState(),
    resetMessage: undefined,
    messageRestImmutableId: undefined,
    fileIdMap: undefined,
    maxMessageSize: undefined,
})

export function MessageProvider(props: PropsWithChildren) {
    const { mailbox } = useContext(AuthorizationContext)
    const { validateReferences } = useContext(FileContext)
    const { t: translate } = useTranslation()

    const [outlookMessage, setOutlookMessage] = useState<Office.MessageRead>()
    const [messageRestId, setMessageRestId] = useState<string>()
    const [messageRestImmutableId, setMessageRestImmutableId] = useState<string>()
    const [fileIdMap, setFileIdMap] = useState<Map<string, string>>()

    // Look up the mailbox's configured maximum message size (in bytes) via the Graph
    // getMailTips action. Mail tips report the mailbox's max receive size, which in practice
    // equals the send limit in virtually every tenant. The configured limit rarely (if ever)
    // changes, so the first lookup is cached for the rest of the session; concurrent callers
    // share the same in-flight promise. Resolves to undefined outside Outlook or when the
    // lookup fails — callers should fall back to their own cap.
    const [maxMessageSize] = useEntity<number, IGraphPayload<{ maxMessageSize: number }[]>>(
        () => ({
            method: 'POST',
            url: `/getMailTips`,
            baseURL: Destination.Email,
            data: {
                EmailAddresses: [mailbox],
                MailTipsOptions: 'maxMessageSize',
            },
            headers: { SuppressErrors: true },
        }),
        isOfficeInitialized() ? messageRestId : null,
        response => response?.data?.[0]?.maxMessageSize || MAX_MESSAGE_SIZE,
    )
    const [message] = useEntity<Message>(
        (id: string) => ({
            method: 'GET',
            url: `/messages/${id}`,
            baseURL: Destination.Email,
            params: {
                $select:
                    'id,internetMessageId,conversationId,subject,bodyPreview,webLink,hasAttachments,sender,toRecipients,sentDateTime,receivedDateTime',
                $expand: `singleValueExtendedProperties($filter=id eq '${FILE_REFERENCE_NUMBER_PROP}')`,
            },
            headers: { Prefer: 'IdType="ImmutableId"' },
        }),
        isOfficeInitialized() ? messageRestId : null,
    )

    useEffect(() => {
        if (isOfficeInitialized()) {
            setOutlookMessage(Office.context.mailbox.item)

            if (Office.context.mailbox.addHandlerAsync) {
                Office.context.mailbox.addHandlerAsync(
                    Office.EventType.ItemChanged,
                    () => {
                        console.log('MessageProvider:', 'Detected switch to mailbox item', Office.context.mailbox.item?.subject)
                        setOutlookMessage(Office.context.mailbox.item)
                    },
                    result => {
                        if (result.status === Office.AsyncResultStatus.Failed) {
                            console.log(
                                'MessageProvider:',
                                'Error in switch detection to mailbox item:',
                                result.error.code,
                                result.error.name,
                                result.error.message,
                            )
                            throw new Error(`${result.error.code}  ${result.error.name}: ${result.error.message}`)
                        }
                    },
                )
            } else {
                console.log(
                    'MessageProvider:',
                    translate('file.pivot.document.error.outdated_outlook', {
                        host: Office.context.diagnostics.host,
                        version: Office.context.diagnostics.version,
                        platform: Office.context.diagnostics.platform,
                    }),
                )
                throw new Error(
                    translate('file.pivot.document.error.outdated_outlook', {
                        host: Office.context.diagnostics.host,
                        version: Office.context.diagnostics.version,
                        platform: Office.context.diagnostics.platform,
                    }),
                )
            }
        } else {
            setOutlookMessage(undefined)
        }
    }, [])

    useEffect(() => {
        if (outlookMessage?.itemId && isOfficeInitialized()) {
            setMessageRestId(
                Office.context.mailbox.convertToRestId(outlookMessage?.itemId, Office.MailboxEnums.RestVersion.v2_0).replaceAll('/', '-'),
            )
        } else {
            setMessageRestId(undefined)
        }
    }, [outlookMessage])

    useEffect(() => {
        if (message.loaded) {
            const references = extractReferenceNumbersFromMessage(message.data)

            if (references) {
                validateReferences(references)
                    .then(ids => (ids?.size === 0 ? setFileIdMap(undefined) : setFileIdMap(ids)))
                    .then(() => setMessageRestImmutableId(message.data?.id))
            } else {
                setFileIdMap(undefined)
                setMessageRestImmutableId(message.data?.id)
            }
        }
    }, [message?.data])

    return (
        <MessageContext.Provider
            value={{
                outlookMessage,
                message,
                resetMessage: () => setMessageRestId(undefined),
                fileIdMap,
                messageRestImmutableId,
                maxMessageSize,
            }}
        >
            {props.children}
        </MessageContext.Provider>
    )
}

export default MessageContext
